import {
  type InstanceModuleMap,
  isCapabilityAvailable,
  isInstanceModuleKey,
  isModuleEnabled,
} from '../../lib/instanceModules';

export type RoleEditorCapabilityGroup = {
  key: string;
  label: string;
  description: string;
  color: string;
  perms: Array<{ key: string; label: string; hint: string }>;
};

/** Editor chrome for Settings → Rollen & Rechte (keys must match the capability catalog). */
export const ROLE_EDITOR_WEB_GROUPS: RoleEditorCapabilityGroup[] = [
  {
    key: 'projekte',
    label: 'Projekte',
    description: 'Anfragen, Angebote, Pipeline',
    color: 'text-sky-600 bg-sky-50 border-sky-200',
    perms: [
      { key: 'web.projekte.view', label: 'Ansehen', hint: 'Projekte und Angebote sehen' },
      { key: 'web.projekte.edit', label: 'Bearbeiten', hint: 'Anlegen, ändern, Status setzen' },
    ],
  },
  {
    key: 'rechnungen',
    label: 'Rechnungen',
    description: 'Rechnungen, Zahlungen, Mahnungen',
    color: 'text-app-text-subtle bg-app-surface-muted border-app-border',
    perms: [
      { key: 'web.rechnungen.view', label: 'Ansehen', hint: 'Rechnungen und Belege sehen' },
      { key: 'web.rechnungen.edit', label: 'Bearbeiten', hint: 'Rechnungen erstellen und ändern' },
      { key: 'web.rechnungen.approve', label: 'Eingangsrechnungen freigeben', hint: 'Eingangsrechnungen aus der Prüfung freigeben' },
      { key: 'web.rechnungen.datev', label: 'DATEV-Export', hint: 'DATEV einrichten und Buchungen an die Kanzlei übergeben' },
    ],
  },
  {
    key: 'kontakte',
    label: 'Kontakte',
    description: 'Kunden, Organisationen, Ansprechpartner',
    color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    perms: [
      { key: 'web.kontakte.view', label: 'Ansehen', hint: 'Kontakte und Organisationen sehen' },
      { key: 'web.kontakte.edit', label: 'Bearbeiten', hint: 'Kontakte anlegen und pflegen' },
    ],
  },
  {
    key: 'plantafel',
    label: 'Plantafel',
    description: 'Termine und Einsatzplanung',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    perms: [
      { key: 'web.plantafel.view', label: 'Ansehen', hint: 'Planung und Termine sehen' },
      { key: 'web.plantafel.edit', label: 'Bearbeiten', hint: 'Planen, verschieben, zuweisen' },
    ],
  },
  {
    key: 'aufgaben',
    label: 'To-dos',
    description: 'To-dos im Team',
    color: 'text-green-600 bg-green-50 border-green-200',
    perms: [
      { key: 'web.aufgaben.own', label: 'Eigene', hint: 'Eigene To-dos sehen, anlegen und erledigen' },
      { key: 'web.aufgaben.view', label: 'Alle ansehen', hint: 'Alle To-dos im Team sehen' },
      { key: 'web.aufgaben.edit', label: 'Alle bearbeiten', hint: 'Fremde To-dos bearbeiten, zuweisen und löschen' },
    ],
  },
  {
    key: 'posteingang',
    label: 'Posteingang',
    description: 'Gemeinsames E-Mail-Postfach',
    color: 'text-cyan-600 bg-cyan-50 border-cyan-200',
    perms: [
      { key: 'web.posteingang.view', label: 'Ansehen', hint: 'E-Mails und Threads lesen' },
      { key: 'web.posteingang.edit', label: 'Bearbeiten', hint: 'Antworten, senden und verwalten' },
    ],
  },
  {
    key: 'kichat',
    label: 'KI Chat',
    description: 'KI-Assistent im Backoffice',
    color: 'text-violet-600 bg-violet-50 border-violet-200',
    perms: [{ key: 'web.kichat.view', label: 'Zugriff', hint: 'Office-KI öffnen und nutzen' }],
  },
  {
    key: 'stammdaten',
    label: 'Stammdaten',
    description: 'Materialien, Leistungen, Preise',
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    perms: [
      { key: 'web.stammdaten.view', label: 'Ansehen', hint: 'Stammdaten durchsuchen' },
      { key: 'web.stammdaten.edit', label: 'Bearbeiten', hint: 'Materialien und Leistungen pflegen' },
    ],
  },
  {
    key: 'ausschreibungen',
    label: 'Ausschreibungen',
    description: 'GAEB / Leistungsverzeichnisse',
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    perms: [
      { key: 'web.ausschreibungen.view', label: 'Ansehen', hint: 'Ausschreibungen öffnen' },
      { key: 'web.ausschreibungen.edit', label: 'Bearbeiten', hint: 'Bearbeiten und importieren' },
    ],
  },
  {
    key: 'vorlagen',
    label: 'Vorlagen',
    description: 'Dokument- und E-Mail-Vorlagen',
    color: 'text-rose-600 bg-rose-50 border-rose-200',
    perms: [
      { key: 'web.vorlagen.view', label: 'Ansehen', hint: 'Vorlagen sehen' },
      { key: 'web.vorlagen.edit', label: 'Bearbeiten', hint: 'Vorlagen erstellen und ändern' },
    ],
  },
  {
    key: 'fuhrpark',
    label: 'Fuhrpark',
    description: 'Fahrzeuge und Fuhrparkverwaltung',
    color: 'text-app-text-muted bg-app-surface-muted border-app-border',
    perms: [
      { key: 'web.fuhrpark.view', label: 'Ansehen', hint: 'Fuhrpark und Fahrzeuge einsehen' },
      { key: 'web.fuhrpark.edit', label: 'Bearbeiten', hint: 'Fahrzeuge anlegen und pflegen' },
    ],
  },
  {
    key: 'hr',
    label: 'Mitarbeiter',
    description: 'Mitarbeiter, Zeit, Lohn',
    color: 'text-amber-700 bg-amber-50 border-amber-200',
    perms: [
      { key: 'web.hr.view', label: 'Ansehen', hint: 'Mitarbeiter und Zeitdaten sehen' },
      { key: 'web.hr.edit', label: 'Bearbeiten', hint: 'Mitarbeiter und HR-Daten pflegen' },
      { key: 'web.hr.approve', label: 'Lohn freigeben', hint: 'Lohnperioden und Änderungsanfragen freigeben' },
      { key: 'web.hr.internal', label: 'Interne (Büro)', hint: 'Innendienst-Mitarbeiter sehen und bearbeiten. Ohne dieses Recht nur Außendienst.' },
    ],
  },
  {
    key: 'einstellungen',
    label: 'Einstellungen',
    description: 'Firma, Nutzer, Rollen & Rechte',
    color: 'text-teal-700 bg-teal-50 border-teal-200',
    perms: [
      { key: 'web.einstellungen.view', label: 'Ansehen', hint: 'Unternehmens-Einstellungen sehen' },
      { key: 'web.einstellungen.edit', label: 'Bearbeiten', hint: 'Rollen, Rechte und Firmendaten ändern' },
      { key: 'web.einstellungen.invite', label: 'Nutzer einladen', hint: 'Neue Teammitglieder per E-Mail oder Telefon einladen' },
    ],
  },
];

export const ROLE_EDITOR_APP_GROUPS: RoleEditorCapabilityGroup[] = [
  {
    key: 'zeit',
    label: 'Zeiterfassung',
    description: 'Stempeln und Zeiteinträge in der App',
    color: 'text-purple-600 bg-purple-50 border-purple-200',
    perms: [
      { key: 'app.zeit.view', label: 'Einträge sehen', hint: 'Eigene Zeiteinträge in der App ansehen (ohne Ein-/Ausstempeln)' },
      { key: 'app.zeit.edit', label: 'Einträge bearbeiten', hint: 'Einstempeln und Ausstempeln in der App' },
      {
        key: 'app.zeit.geofence_in',
        label: 'Einstempeln nur vor Ort',
        hint: 'Einstempeln nur im Umkreis des ersten Termins des Tages',
      },
      {
        key: 'app.zeit.geofence_out',
        label: 'Ausstempeln nur vor Ort',
        hint: 'Ausstempeln nur im Umkreis des letzten Termins des Tages',
      },
      {
        key: 'app.zeit.team_edit',
        label: 'Zeiten fürs Team eintragen',
        hint: 'Obermonteur: Arbeitszeiten für Kollegen beantragen, die am selben Tag im selben Termin eingeplant sind (Freigabe durchs Büro)',
      },
    ],
  },
  {
    key: 'kalender',
    label: 'Kalender',
    description: 'Kalender ansehen, Termine bearbeiten oder hinzufügen',
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    perms: [
      { key: 'app.kalender.view', label: 'Kalender ansehen', hint: 'Kalender in der App öffnen und anzeigen' },
      { key: 'app.kalender.update', label: 'Termin bearbeiten', hint: 'Angaben eines zugewiesenen Termins in der App ändern' },
      { key: 'app.kalender.edit', label: 'Termin hinzufügen', hint: 'Neue Termine zum Kalender hinzufügen' },
      {
        key: 'app.kalender.team_view',
        label: 'Team-Termine sehen',
        hint: 'Im Kalender sehen, wer wann wo eingeplant ist (ohne Kundenkontakte und Preise)',
      },
    ],
  },
  {
    key: 'kontakte',
    label: 'Kontakte',
    description: 'Kontaktdaten im Außendienst',
    color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    perms: [
      { key: 'app.kontakte.view', label: 'Kontakte einsehen', hint: 'Kunden und Ansprechpartner in der App sehen' },
    ],
  },
  {
    key: 'preise',
    label: 'Preise',
    description: 'Preisinformationen in der App',
    color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    perms: [
      { key: 'app.preise.view', label: 'Preise anzeigen', hint: 'Preise und Kalkulationen in der App sehen' },
    ],
  },
  {
    key: 'rechnungen',
    label: 'Rechnungen',
    description: 'Rechnungen in der Field-App erstellen',
    color: 'text-teal-600 bg-teal-50 border-teal-200',
    perms: [
      {
        key: 'app.rechnungen.create',
        label: 'Rechnung erstellen',
        hint: 'Rechnungen mit Positionen und Unterschrift in der App erzeugen (Standard: nur Inhaber, Admin, Büro Vollzugriff)',
      },
    ],
  },
  {
    key: 'kichat',
    label: 'KI Chat',
    description: 'KI-Assistent in der Field-App',
    color: 'text-violet-600 bg-violet-50 border-violet-200',
    perms: [
      {
        key: 'app.kichat.angebote',
        label: 'KI-Angebote erstellen',
        hint: 'Im Chat eines Auftrags Angebote anlegen, Positionen ändern und vor Ort unterschreiben lassen. Mailversand braucht zusätzlich „Posteingang bearbeiten“ (Web).',
      },
    ],
  },
  {
    key: 'kommentare',
    label: 'Kommentare',
    description: 'Projekt-Kommentare in der App',
    color: 'text-sky-600 bg-sky-50 border-sky-200',
    perms: [
      { key: 'app.kommentare.view', label: 'Kommentare sehen', hint: 'Projekt-Notizen und Kommentare bei zugewiesenen Terminen lesen' },
      {
        key: 'app.kommentare.edit',
        label: 'Kommentare schreiben',
        hint: 'Kommentare (auch per Diktat) schreiben und Kollegen mit @ erwähnen; erscheinen als Notiz im Projekt',
      },
    ],
  },
];

type RoleEditorModules = Partial<InstanceModuleMap> | null | undefined;

/** Gruppen abgewählter Module (Gruppen-Key = Modul-Key) und nicht verfügbare Rechte entfernen. */
function filterGroupsForModules(
  groups: RoleEditorCapabilityGroup[],
  modules: RoleEditorModules,
  keepPerm: (permKey: string) => boolean = () => true,
): RoleEditorCapabilityGroup[] {
  return groups
    .filter((group) => !isInstanceModuleKey(group.key) || isModuleEnabled(modules, group.key))
    .map((group) => ({
      ...group,
      perms: group.perms.filter((perm) => keepPerm(perm.key) && isCapabilityAvailable(perm.key, modules)),
    }));
}

/**
 * Web-Gruppen für Rollen- und Nutzerdialoge: Gruppen und Rechte abgewählter Module (Admin Center)
 * fehlen ganz. `datevEnabled: false` blendet zusätzlich das DATEV-Recht aus, solange die
 * Integration in der Instanz nicht aktiv ist.
 */
export function webGroupsFor(
  modules: RoleEditorModules,
  options: { datevEnabled?: boolean } = {},
): RoleEditorCapabilityGroup[] {
  return filterGroupsForModules(
    ROLE_EDITOR_WEB_GROUPS,
    modules,
    (permKey) => options.datevEnabled !== false || permKey !== 'web.rechnungen.datev',
  );
}

/** App-Gruppen: ohne Mobile-App-Modul keine, sonst wie die Web-Gruppen gefiltert. */
export function appGroupsFor(modules: RoleEditorModules): RoleEditorCapabilityGroup[] {
  if (!isModuleEnabled(modules, 'field_app')) return [];
  return filterGroupsForModules(ROLE_EDITOR_APP_GROUPS, modules);
}

/** Rechte-Keys, die Rollen- und Nutzerdialoge in dieser Instanz anbieten (für flache Rechtelisten). */
export function availableRoleEditorPermKeys(
  modules: RoleEditorModules,
  options: { datevEnabled?: boolean } = {},
): Set<string> {
  return new Set(
    [...webGroupsFor(modules, options), ...appGroupsFor(modules)].flatMap((group) => group.perms.map((perm) => perm.key)),
  );
}
